import { useState } from 'react';
import { getLogger } from '@/idv2/utils/logger';
import type { CameraErrorCause } from '../../types';
import getCameraErrorCause from '../../utils/get-camera-error-cause';
import { getMediaStream } from '../camera/hooks/use-user-media';
import CameraAccessRequestView from './camera-access-request-view';

type CameraAccessRequestProps = {
  onClose: () => void;
  onCameraError: (cause: CameraErrorCause) => void;
  onCameraAccessDenied: () => void;
  onSuccess: () => void;
  variant?: 'document' | 'selfie';
};

const { logWarn } = getLogger({ name: 'idv2', location: 'camera-access-request' });

const CameraAccessRequest = ({
  onClose,
  onCameraError,
  onCameraAccessDenied,
  onSuccess,
  variant,
}: CameraAccessRequestProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleAllowAccess = async () => {
    setIsLoading(true);
    try {
      const stream = await getMediaStream({ video: true });
      stream.getTracks().forEach(track => {
        track.stop();
      });
      onSuccess();
    } catch (err) {
      logWarn('Error getting media stream', err);
      const cause = getCameraErrorCause(err);
      if (cause === 'permission-denied') {
        onCameraAccessDenied();
      } else {
        onCameraError(cause);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CameraAccessRequestView
      onClose={onClose}
      onAllowAccess={handleAllowAccess}
      isLoading={isLoading}
      variant={variant}
    />
  );
};

export default CameraAccessRequest;
